/**
 * CapExFundingComparison — Phase 205 (enhanced Phase 213-02)
 *
 * 3-column comparison of CapEx funding strategies during the construction period.
 * Shows after-tax returns impact (OBBB Year 1 benefit) and effective equity per path.
 * Modeled after BonusDepreciationComparison pattern.
 *
 * Covers construction period ONLY — ends at stabilization where permanent
 * financing begins. Explicit handoff label at bottom.
 */

import type { CapExFundingComparison as CapExFundingComparisonType, CapExFundingPath } from '../../types/calculations';

/**
 * OBBB bonus depreciation constants (mirrors config.py SSOT values).
 * BONUS_DEPRECIATION_RATE = 1.00 (100% immediate expensing, OBBB 2025-2026)
 * EFFECTIVE_TAX_RATE = 0.40 (40% combined federal + PA state)
 */
const BONUS_DEPRECIATION_RATE = 1.0;
const EFFECTIVE_TAX_RATE = 0.40;

interface Props {
  comparison: CapExFundingComparisonType;
  stabilizedNoi?: number;       // Annual stabilized NOI for actual CoC calculation
  annualDebtService?: number;   // Annual debt service for actual CoC calculation
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// formatPct removed — CoC percentages replaced with dollar amounts per path

/**
 * Returns the investor-facing one-liner for a given path name (REQ-3).
 * These are fixed copy keyed on path name — not derived from backend description.
 */
function getInvestorRationale(pathName: string): string {
  if (pathName === 'Capital Call') {
    return 'Higher equity, but simplest execution. Full depreciation benefit on Day 1.';
  }
  if (pathName === 'Construction-to-Perm') {
    return 'Lowest total cost. Interest only on drawn amounts. Recommended for most deals.';
  }
  if (pathName.startsWith('Hard Money')) {
    return 'BRRRR model: HM funds purchase + rehab. Refi to perm at stabilized ARV. Highest leverage, fastest close.';
  }
  return pathName;
}

interface PathColumnProps {
  path: CapExFundingPath;
  isRecommended: boolean;
  isWorst: boolean;
  obbbBenefit: number;
  savingsVsWorst: number | null;
  stabilizedNoi?: number;
  annualDebtService?: number;
}

function PathColumn({ path, isRecommended, isWorst, obbbBenefit, savingsVsWorst, stabilizedNoi, annualDebtService }: PathColumnProps) {
  // Baseline pre-tax cash flow (same for all paths — NOI minus debt service)
  const baselineCF = stabilizedNoi != null && annualDebtService != null
    ? stabilizedNoi - annualDebtService
    : null;
  const rationale = getInvestorRationale(path.name);

  return (
    <div
      className={`rounded-lg border p-4 flex flex-col gap-0 ${
        isRecommended
          ? 'border-amber-400 bg-amber-500/10 ring-2 ring-amber-400/40'
          : isWorst
          ? 'border-stone-700 bg-stone-800/30 opacity-80'
          : 'border-stone-700 bg-stone-800/50'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-stone-100 text-sm">{path.name}</h4>
        {isRecommended && (
          <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-400/25 text-amber-300 border border-amber-400/40">
            Best Value
          </span>
        )}
      </div>

      {/* Investor rationale (REQ-3) */}
      <p className="text-xs text-stone-400 mb-3 leading-relaxed">{rationale}</p>

      {/* Savings vs worst (REQ-4) — only on recommended */}
      {isRecommended && savingsVsWorst !== null && savingsVsWorst > 0 && (
        <div className="mb-3 rounded bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1.5">
          <span className="text-xs font-semibold text-emerald-400">
            Saves {formatCurrency(savingsVsWorst)} vs worst option
          </span>
        </div>
      )}

      {/* Structured breakdown */}
      <div className="space-y-2 text-sm">
        {/* What you put in */}
        <div className="text-xs text-stone-400 uppercase tracking-wider mb-1">Equity In</div>
        <div className="flex justify-between">
          <span className="text-stone-400">Total Equity</span>
          <span className="text-stone-100 tabular-nums font-semibold">{formatCurrency(path.equity_at_close)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-stone-500 text-xs pl-2">Closings</span>
          <span className="text-stone-400 tabular-nums text-xs">
            {path.closing_events} closing{path.closing_events > 1 ? 's' : ''} ({formatCurrency(path.closing_costs)})
          </span>
        </div>

        {/* What it costs during construction */}
        <div className="border-t border-stone-700 mt-2 pt-2">
          <div className="text-xs text-stone-400 uppercase tracking-wider mb-1">Construction Cost</div>
          <div className="flex justify-between">
            <span className="text-stone-400">Interest</span>
            <span className="text-red-400 tabular-nums font-medium">−{formatCurrency(path.total_construction_interest)}</span>
          </div>
          {path.total_carry_cost > path.total_construction_interest && (
            <div className="flex justify-between">
              <span className="text-stone-400">Points + Refi Costs</span>
              <span className="text-red-400 tabular-nums font-medium">
                −{formatCurrency(path.total_carry_cost - path.total_construction_interest)}
              </span>
            </div>
          )}
          <div className="flex justify-between mt-1 pt-1 border-t border-stone-700/50">
            <span className="text-stone-300 font-medium">Total Carry</span>
            <span className={`tabular-nums font-semibold ${isRecommended ? 'text-emerald-400' : 'text-red-400'}`}>
              −{formatCurrency(path.total_carry_cost)}
            </span>
          </div>
        </div>

        {/* BRRRR Hard Money breakdown — CRH-02 */}
        {path.name.startsWith('Hard Money') && path.arv_estimate != null && (
          <div className="border-t border-stone-700 mt-2 pt-2">
            <div className="text-xs text-stone-400 uppercase tracking-wider mb-1">BRRRR Deal Structure</div>
            {/* HM Loan with purchase + CapEx split */}
            <div className="flex justify-between">
              <span className="text-stone-400">HM Loan (full basis)</span>
              <span className="text-stone-100 tabular-nums font-semibold">{formatCurrency(path.perm_loan_amount != null ? path.equity_at_close + path.perm_loan_amount : 0)}</span>
            </div>
            {path.purchase_contribution != null && (
              <div className="flex justify-between">
                <span className="text-stone-500 text-xs pl-2">Purchase</span>
                <span className="text-stone-400 tabular-nums text-xs">{formatCurrency(path.purchase_contribution)}</span>
              </div>
            )}
            {path.capex_contribution != null && (
              <div className="flex justify-between">
                <span className="text-stone-500 text-xs pl-2">CapEx / Rehab</span>
                <span className="text-stone-400 tabular-nums text-xs">{formatCurrency(path.capex_contribution)}</span>
              </div>
            )}
            {/* ARV and refi */}
            <div className="flex justify-between mt-1">
              <span className="text-stone-400">ARV Estimate</span>
              <span className="text-blue-400 tabular-nums">{formatCurrency(path.arv_estimate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-400">Refi Perm Loan</span>
              <span className="text-stone-200 tabular-nums">{path.perm_loan_amount != null ? formatCurrency(path.perm_loan_amount) : '—'}</span>
            </div>
            {path.hm_payoff_at_refi != null && (
              <div className="flex justify-between">
                <span className="text-stone-400">HM Payoff at Refi</span>
                <span className="text-stone-300 tabular-nums">{formatCurrency(path.hm_payoff_at_refi)}</span>
              </div>
            )}
            {/* Cash-in-refi warning (amber) */}
            {path.cash_in_refi != null && path.cash_in_refi > 0 && (
              <div className="mt-2 rounded bg-amber-500/10 border border-amber-500/20 px-2.5 py-1.5">
                <p className="text-xs text-amber-400 font-medium">
                  Cash-in at Refi: {formatCurrency(path.cash_in_refi)}
                </p>
                <p className="text-xs text-amber-400/70 mt-0.5">
                  Perm loan doesn&apos;t fully cover HM payoff — bring additional cash at refi
                </p>
              </div>
            )}
          </div>
        )}

        {/* Year 1 cash flow impact */}
        <div className="border-t border-stone-700 mt-2 pt-2">
          <div className="text-xs text-stone-400 uppercase tracking-wider mb-1">Year 1 Impact</div>
          {baselineCF != null && (
            <>
              <div className="flex justify-between">
                <span className="text-stone-400">Deal Pre-Tax CF</span>
                <span className="text-stone-200 tabular-nums">{formatCurrency(baselineCF)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-400">Less: Carry Cost</span>
                <span className="text-red-400 tabular-nums">−{formatCurrency(path.total_carry_cost)}</span>
              </div>
              <div className="flex justify-between mt-1 pt-1 border-t border-stone-700/50">
                <span className="text-stone-300 font-medium">Yr 1 Net CF</span>
                <span className={`tabular-nums font-semibold ${(baselineCF - path.total_carry_cost) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(baselineCF - path.total_carry_cost)}
                </span>
              </div>
            </>
          )}
          <div className="flex justify-between mt-1">
            <span className="text-stone-400">OBBB Tax Benefit</span>
            <span className="text-emerald-400 tabular-nums font-medium">+{formatCurrency(obbbBenefit)}</span>
          </div>
          {baselineCF != null && (
            <div className="flex justify-between mt-1 pt-1 border-t border-stone-700/50">
              <span className="text-stone-300 font-medium">Yr 1 After-Tax CF</span>
              <span className="text-emerald-400 tabular-nums font-semibold">
                {formatCurrency(baselineCF - path.total_carry_cost + obbbBenefit)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CapExFundingComparison({ comparison, stabilizedNoi, annualDebtService }: Props) {
  const { paths, recommended_path, recommendation_reasoning, stabilization_handoff, construction_months, capex_budget } = comparison;

  // REQ-1: OBBB Year 1 benefit is the same for all paths — depreciation basis is the CapEx budget
  // Formula: capex_budget * BONUS_DEPRECIATION_RATE * EFFECTIVE_TAX_RATE
  const obbbBenefit = Math.round(capex_budget * BONUS_DEPRECIATION_RATE * EFFECTIVE_TAX_RATE);

  // REQ-4: Identify worst path (highest carry cost) for savings calculation
  const worstPath = paths.reduce((worst, p) => (p.total_carry_cost > worst.total_carry_cost ? p : worst), paths[0]);
  const recommendedPath = paths.find(p => p.name === recommended_path);
  const savingsVsWorst = recommendedPath && worstPath
    ? worstPath.total_carry_cost - recommendedPath.total_carry_cost
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-stone-100">
          CapEx Funding Comparison
        </h3>
        <span className="text-sm text-stone-400">
          {formatCurrency(capex_budget)} over {construction_months} months
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {paths.map((path) => {
          const isRecommended = path.name === recommended_path;
          const isWorst = path.name === worstPath?.name && !isRecommended;
          return (
            <PathColumn
              key={path.name}
              path={path}
              isRecommended={isRecommended}
              isWorst={isWorst}
              obbbBenefit={obbbBenefit}
              savingsVsWorst={isRecommended ? savingsVsWorst : null}
              stabilizedNoi={stabilizedNoi}
              annualDebtService={annualDebtService}
            />
          );
        })}
      </div>

      {/* Recommendation reasoning */}
      <div className="rounded-lg border border-stone-700 bg-stone-800/30 p-3">
        <p className="text-sm text-stone-300 leading-relaxed">{recommendation_reasoning}</p>
      </div>

      {/* OBBB context note */}
      <div className="rounded-lg border border-emerald-800/40 bg-emerald-900/10 p-3">
        <p className="text-xs text-emerald-400/80 leading-relaxed">
          <span className="font-semibold text-emerald-400">OBBB Depreciation Note:</span>{' '}
          All three paths qualify for the same {formatCurrency(obbbBenefit)} Year 1 bonus depreciation benefit —
          the depreciation basis is the CapEx budget, not the financing method.
          The difference between paths is carry cost and equity required.
        </p>
      </div>

      {/* Stabilization handoff boundary */}
      <div className="flex items-center gap-2 py-2">
        <div className="flex-1 border-t border-dashed border-amber-500/30" />
        <span className="text-sm text-amber-400 font-medium whitespace-nowrap">
          {stabilization_handoff}
        </span>
        <div className="flex-1 border-t border-dashed border-amber-500/30" />
      </div>
    </div>
  );
}
